<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcSiteSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    path: {
      type: String,
      required: true,
    },
    parameters: {
      type: Object,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    autofocus: {
      type: Boolean,
      required: false,
    },
    initialOptions: {
      type: Array,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const initialOptionsInternal = ref(null);
  const ux = ref([]);
  const b_7315092382398562_dataSource = computed(() => ({
    name: 'suggest_property',
    parameters: props.parameters,
  }));
  const b_7315092382398562_optionTemplate = computed(
    () =>
      '<div>\n\
  {{ option.name }}\n\
  <span :if="option.description">- {{ option.description }}</span>\n\
</div>'
  );

  const $data = computed(() => ({
    initialOptionsInternal: initialOptionsInternal.value,
    ux: ux.value,
    b_7315092382398562_dataSource: b_7315092382398562_dataSource.value,
    b_7315092382398562_optionTemplate: b_7315092382398562_optionTemplate.value,
  }));

  const $$load_initialOptionsInternal = async () => {
    return (
      props.initialOptions ||
      (props.item[props.path] ? [props.item[props.path]] : [])
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'initialOptionsInternal' },
      { name: 'ux' },
      { name: 'b_7315092382398562_dataSource', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_optionTemplate', nodeId: '7315092382398562' },
    ],
    loaders: { initialOptionsInternal: $$load_initialOptionsInternal },
    ux: {},
    dataRefs: {
      initialOptionsInternal,
      ux,
      b_7315092382398562_dataSource,
      b_7315092382398562_optionTemplate,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.path,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('path');
    }
  );
  watch(
    () => props.parameters,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parameters');
    }
  );
  watch(
    () => props.required,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('required');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.autofocus,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('autofocus');
    }
  );
  watch(
    () => props.initialOptions,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('initialOptions');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpSelect2
    :name="path"
    v-model="item[path]"
    :value="item[path]"
    @input="
      !($event && typeof $event.stopPropagation === 'function') &&
      (item[path] =
        $event && typeof $event === 'object' && 'data' in $event
          ? $event.data
          : $event)
    "
    :dataSource="b_7315092382398562_dataSource"
    prefetch
    :autofocus="autofocus"
    bind-object
    :disabled="readonly || item.isReadonly"
    :readonly="readonly || item.isReadonly"
    :required="required"
    :initialOptions="initialOptionsInternal"
    valueField="id"
    :optionTemplate="b_7315092382398562_optionTemplate"
    use-option-template-for-selected-option
    v-if="item"
  >
  </ItpSelect2>
</template>
